
import React, { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Dokument {
  id: string;
  title: string;
  contact: string;
  category: string;
  document_date: string;
  file_name: string | null;
  file_url: string | null;
  file_size: number | null;
  notes: string;
  automat: string | null;
  created_at: string;
}

interface Kontakt {
  id: string;
  name: string;
  firma?: string;
}

interface Automaten {
  id: string;
  name: string;
  location?: string;
}

const kategorien = [
  'Rechnung',
  'Vertrag',
  'Angebot',
  'Lieferschein',
  'Mahnung',
  'Steuerbeleg',
  'Zertifikat',
  'Kataloge',
  'Zoll',
  'Auszahlungen',
  'Import',
  'Kaufvertrag',
  'Bedienungsanleitung',
  'Snack Bestellungen',
  'Nayax',
  'Sonstiges'
];

export default function Dokumente() {
  const { user } = useAuth();
  const [dokumente, setDokumente] = useState<Dokument[]>([]);
  const [kontakte, setKontakte] = useState<Kontakt[]>([]);
  const [automaten, setAutomaten] = useState<Automaten[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Dokument | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [suchbegriff, setSuchbegriff] = useState('');
  const [filterKontakt, setFilterKontakt] = useState('');
  const [filterKategorie, setFilterKategorie] = useState('');
  const [filterAutomat, setFilterAutomat] = useState('');
  const [sortierung, setSortierung] = useState<'document_date' | 'title' | 'contact'>('document_date');
  const [sortierrichtung, setSortierrichtung] = useState<'asc' | 'desc'>('desc');
  const [ansichtstyp, setAnsichtstyp] = useState<'tabelle' | 'karten'>('karten');

  // Formular State
  const [formData, setFormData] = useState({
    title: '',
    contact: '',
    category: '',
    automat: '',
    document_date: new Date().toISOString().split('T')[0],
    file: null as File | null,
    notes: ''
  });

  // Edit Form Data
  const [editFormData, setEditFormData] = useState({
    title: '',
    contact: '',
    category: '',
    automat: '',
    document_date: '',
    notes: ''
  });

  // Load contacts from Supabase
  const loadContacts = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_type', 'contact')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading contacts:', error);
        return;
      }

      const contactData = data.map(item => ({
        id: item.id,
        name: item.data.name,
        firma: item.data.firma
      }));

      setKontakte(contactData);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  // Load documents from Supabase
  const loadDocuments = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading documents:', error);
        return;
      }

      setDokumente(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load automaten from Supabase
  const loadAutomaten = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_type', 'automat')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading automaten:', error);
        return;
      }

      const automatenData = data
        .filter(item => {
          return item.data && item.data.machine_type === 'physical_automat';
        })
        .map(item => ({
          id: item.id,
          name: item.data.name,
          location: item.data.location
        }));

      setAutomaten(automatenData);
    } catch (error) {
      console.error('Error loading automaten:', error);
    }
  };

  useEffect(() => {
    if (user) {
      Promise.all([loadDocuments(), loadContacts(), loadAutomaten()]);
    }
  }, [user]);

  // Upload file to Supabase Storage
  const uploadFile = async (file: File): Promise<{ url: string; error?: any }> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user?.id}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (error) {
        return { url: '', error };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      return { url: publicUrl };
    } catch (error) {
      return { url: '', error };
    }
  };

  // Statistiken berechnen
  const aktuellerMonat = new Date().getMonth();
  const aktuellesJahr = new Date().getFullYear();
  
  const dokumenteThisMonth = dokumente.filter(dok => {
    const dokDatum = new Date(dok.document_date);
    return dokDatum.getMonth() === aktuellerMonat && dokDatum.getFullYear() === aktuellesJahr;
  });

  const rechnungenThisMonth = dokumenteThisMonth.filter(dok => dok.category === 'Rechnung');

  // Dokument hinzufügen
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.contact || !formData.category || !user) {
      alert('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setUploading(true);

    try {
      let fileUrl = null;
      let fileName = null;
      let fileSize = null;

      // Upload file if provided
      if (formData.file) {
        console.log('📁 Uploading file:', formData.file.name);
        const { url, error } = await uploadFile(formData.file);
        if (error) {
          console.error('❌ Error uploading file:', error);
          alert('Fehler beim Hochladen der Datei: ' + error.message);
          setUploading(false);
          return;
        }
        fileUrl = url;
        fileName = formData.file.name;
        fileSize = formData.file.size;
        console.log('✅ File uploaded successfully:', url);
      }

      const documentData = {
        user_id: user.id,
        title: formData.title.trim(),
        contact: formData.contact.trim(),
        category: formData.category.trim(),
        automat: formData.automat && formData.automat.trim() !== '' ? formData.automat.trim() : null,
        document_date: formData.document_date,
        file_name: fileName,
        file_url: fileUrl,
        file_size: fileSize,
        notes: formData.notes.trim() || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('💾 Final document data for insert:', documentData);

      const { data, error } = await supabase
        .from('documents')
        .insert([{
          user_id: documentData.user_id,
          title: documentData.title,
          contact: documentData.contact,
          category: documentData.category,
          automat: documentData.automat,
          document_date: documentData.document_date,
          file_name: documentData.file_name,
          file_url: documentData.file_url,
          file_size: documentData.file_size,
          notes: documentData.notes,
          created_at: documentData.created_at,
          updated_at: documentData.updated_at
        }])
        .select()
        .single();

      if (error) {
        console.error('❌ Database error details:', error);
        alert('Fehler beim Speichern des Dokuments: ' + error.message);
        return;
      }

      if (!data) {
        console.error('❌ No data returned from insert');
        alert('Fehler: Keine Daten vom Server zurückerhalten');
        return;
      }

      console.log('✅ Document saved successfully:', data);

      // Add to local state
      setDokumente([data, ...dokumente]);
      
      // Reset form
      setFormData({
        title: '',
        contact: '',
        category: '',
        automat: '',
        document_date: new Date().toISOString().split('T')[0],
        file: null,
        notes: ''
      });
      setIsModalOpen(false);
      alert('✅ Dokument erfolgreich gespeichert!');
    } catch (error) {
      console.error('❌ Unexpected error creating document:', error);
      alert('Unerwarteter Fehler beim Erstellen des Dokuments: ' + (error as Error).message);
    } finally {
      setUploading(false);
    }
  };

  // Dokument löschen
  const handleDelete = async (id: string) => {
    if (!confirm('Dokument wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting document:', error);
        return;
      }

      setDokumente(dokumente.filter(dok => dok.id !== id));
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  // Datei-Upload Handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, file }));
  };

  // Download file
  const handleDownload = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Dokument bearbeiten
  const handleEdit = (dokument: Dokument) => {
    setEditingDocument(dokument);
    setEditFormData({
      title: dokument.title,
      contact: dokument.contact,
      category: dokument.category,
      automat: dokument.automat || '',
      document_date: dokument.document_date,
      notes: dokument.notes
    });
    setIsEditModalOpen(true);
  };

  // Dokument aktualisieren
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingDocument || !user) return;

    setUpdating(true);

    try {
      const updateData = {
        title: editFormData.title.trim(),
        contact: editFormData.contact.trim(),
        category: editFormData.category.trim(),
        automat: editFormData.automat && editFormData.automat.trim() !== '' ? editFormData.automat.trim() : null,
        document_date: editFormData.document_date,
        notes: editFormData.notes.trim() || '',
        updated_at: new Date().toISOString()
      };

      console.log('🔄 Update data for document:', updateData);

      const { data, error } = await supabase
        .from('documents')
        .update(updateData)
        .eq('id', editingDocument.id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating document:', error);
        alert('Fehler beim Aktualisieren des Dokuments: ' + error.message);
        return;
      }

      if (!data) {
        console.error('❌ No data returned from update');
        alert('Fehler: Keine Daten vom Server zurückerhalten');
        return;
      }

      console.log('✅ Document updated successfully:', data);

      // Update local state
      setDokumente(dokumente.map(dok => 
        dok.id === editingDocument.id ? { ...dok, ...data } : dok
      ));
      
      setIsEditModalOpen(false);
      setEditingDocument(null);
      alert('✅ Dokument erfolgreich aktualisiert!');
    } catch (error) {
      console.error('❌ Unexpected error updating document:', error);
      alert('Unerwarteter Fehler beim Aktualisieren des Dokuments: ' + (error as Error).message);
    } finally {
      setUpdating(false);
    }
  };

  // Gefilterte und sortierte Dokumente
  const gefilterte_dokumente = dokumente
    .filter(dok => {
      const matchesSuch = dok.title.toLowerCase().includes(suchbegriff.toLowerCase()) ||
                        dok.notes.toLowerCase().includes(suchbegriff.toLowerCase());
      const matchesKontakt = !filterKontakt || dok.contact === filterKontakt;
      const matchesKategorie = !filterKategorie || dok.category === filterKategorie;
      const matchesAutomat = !filterAutomat || dok.automat === filterAutomat;
      
      return matchesSuch && matchesKontakt && matchesKategorie && matchesAutomat;
    })
    .sort((a, b) => {
      let vergleich = 0;
      
      switch (sortierung) {
        case 'document_date':
          vergleich = new Date(a.document_date).getTime() - new Date(b.document_date).getTime();
          break;
        case 'title':
          vergleich = a.title.localeCompare(b.title);
          break;
        case 'contact':
          vergleich = a.contact.localeCompare(b.contact);
          break;
      }
      
      return sortierrichtung === 'desc' ? -vergleich : vergleich;
    });

  // Gruppierte Dokumente nach Kunde und Kategorie
  const gruppierte_dokumente = gefilterte_dokumente.reduce((acc, dok) => {
    const kunde = dok.contact;
    const kategorie = dok.category;
    
    if (!acc[kunde]) {
      acc[kunde] = {};
    }
    if (!acc[kunde][kategorie]) {
      acc[kunde][kategorie] = [];
    }
    acc[kunde][kategorie].push(dok);
    
    return acc;
  }, {} as Record<string, Record<string, Dokument[]>>);

  // Kontakt-Optionen aus echten Kontakten erstellen
  const kontaktOptionen = kontakte.map(kontakt => ({
    value: kontakt.name,
    label: kontakt.firma ? `${kontakt.name} (${kontakt.firma})` : kontakt.name
  }));

  const kategorieOptionen = kategorien.map(kat => ({
    value: kat,
    label: kat
  }));

  const automatenOptionen = automaten.map(automat => ({
    value: automat.name,
    label: automat.location ? `${automat.name} (${automat.location})` : automat.name
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile-optimized Header */}
      <div className="border-b border-gray-200">
        <div className="w-full px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center sm:hidden">
                <i className="ri-file-text-line text-blue-600 text-lg"></i>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Dokumente</h1>
                <p className="text-sm text-gray-600 mt-1">
                  <span className="inline-block">{dokumente.length} Dokumente</span>
                  <span className="hidden sm:inline"> • </span>
                  <span className="block sm:inline">{dokumenteThisMonth.length} diesen Monat</span>
                  <span className="hidden sm:inline"> • </span>
                  <span className="block sm:inline">{rechnungenThisMonth.length} Rechnungen</span>
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between sm:justify-end space-x-3">
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-100 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-green-700">Supabase</span>
              </div>
              <Button
                onClick={() => setIsModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center"
              >
                <i className="ri-add-line mr-2"></i>
                <span className="hidden sm:inline">Hinzufügen</span>
                <span className="sm:hidden">Neu</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-3 sm:px-6 py-4 sm:py-6">
        {dokumente.length === 0 ? (
          // Mobile-optimized Empty State
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-file-text-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2 text-center">Keine Dokumente vorhanden</h3>
            <p className="text-gray-600 text-center mb-6 max-w-sm text-sm leading-relaxed">
              Laden Sie Ihr erstes Dokument hoch, um mit der Verwaltung Ihrer Unterlagen zu beginnen.
            </p>
            <Button
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Erstes Dokument hinzufügen
            </Button>
          </div>
        ) : (
          <>
            {/* Mobile-optimized Filter und Suche */}
            <div className="mb-6 space-y-4">
              {/* Search Bar - Full Width on Mobile */}
              <div className="w-full">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Dokumente durchsuchen..."
                    value={suchbegriff}
                    onChange={(e) => setSuchbegriff(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>

              {/* Mobile Filter Row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <select
                  value={filterKontakt}
                  onChange={(e) => setFilterKontakt(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                >
                  <option value="">Alle Kunden</option>
                  {kontakte.map(kontakt => (
                    <option key={kontakt.id} value={kontakt.name}>
                      {kontakt.firma ? `${kontakt.name} (${kontakt.firma})` : kontakt.name}
                    </option>
                  ))}
                </select>
                <select
                  value={filterKategorie}
                  onChange={(e) => setFilterKategorie(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                >
                  <option value="">Alle Kategorien</option>
                  {kategorien.map(kat => (
                    <option key={kat} value={kat}>{kat}</option>
                  ))}
                </select>
                <select
                  value={filterAutomat}
                  onChange={(e) => setFilterAutomat(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                >
                  <option value="">Alle Automaten</option>
                  {automaten.map(automat => (
                    <option key={automat.id} value={automat.name}>
                      {automat.location ? `${automat.name} (${automat.location})` : automat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* View Toggle - Mobile Optimized */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setAnsichtstyp('karten')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    ansichtstyp === 'karten' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-layout-grid-line mr-2"></i>
                  Karten
                </button>
                <button
                  onClick={() => setAnsichtstyp('tabelle')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    ansichtstyp === 'tabelle' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-list-check mr-2"></i>
                  Liste
                </button>
              </div>
            </div>

            {/* Mobile-optimized Content */}
            {ansichtstyp === 'karten' ? (
              // Mobile-optimized Card View
              <div className="space-y-6">
                {Object.entries(gruppierte_dokumente).map(([kunde, kategorien]) => (
                  <div key={kunde} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    {/* Customer Header - Mobile Optimized */}
                    <div className="px-4 py-4 bg-green-50 border-b border-green-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-green-600"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">{kunde}</h3>
                          <p className="text-sm text-gray-600">
                            {Object.values(kategorien).reduce((total, docs) => total + docs.length, 0)} Dokumente
                          </p>
                        </div>
                      </div>
                      {/* Category Tags - Mobile Optimized */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {Object.entries(kategorien).map(([kategorie, docs]) => (
                          <span key={kategorie} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {kategorie}: {docs.length}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Documents for this Customer - Mobile Optimized */}
                    <div className="p-4 space-y-4">
                      {Object.entries(kategorien).sort(([a], [b]) => a.localeCompare(b)).map(([kategorie, dokumente]) => (
                        <div key={kategorie} className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-md font-medium text-gray-900">{kategorie}</h4>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {dokumente.length}
                            </span>
                          </div>
                          
                          <div className="space-y-3">
                            {dokumente.sort((a, b) => new Date(b.document_date).getTime() - new Date(a.document_date).getTime()).map((dokument) => (
                              <div key={dokument.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                      <i className="ri-file-text-line text-blue-600 text-sm"></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-900 text-sm truncate">{dokument.title}</p>
                                      <p className="text-xs text-gray-500">
                                        {new Date(dokument.document_date).toLocaleDateString('de-DE')}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-1 flex-shrink-0">
                                    {dokument.file_name && dokument.file_url && (
                                      <button 
                                        onClick={() => handleDownload(dokument.file_url!, dokument.file_name!)}
                                        className="p-2 text-gray-400 hover:text-blue-600 cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
                                        title="Herunterladen"
                                      >
                                        <i className="ri-download-line text-sm"></i>
                                      </button>
                                    )}
                                    <button 
                                      onClick={() => handleEdit(dokument)}
                                      className="p-2 text-gray-400 hover:text-green-600 cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
                                      title="Bearbeiten"
                                    >
                                      <i className="ri-edit-line text-sm"></i>
                                    </button>
                                    <button 
                                      onClick={() => handleDelete(dokument.id)}
                                      className="p-2 text-gray-400 hover:text-red-600 cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
                                      title="Löschen"
                                    >
                                      <i className="ri-delete-bin-line text-sm"></i>
                                    </button>
                                  </div>
                                </div>
                                
                                <div className="space-y-2">
                                  {dokument.file_name && (
                                    <p className="text-xs text-gray-600 flex items-center">
                                      <i className="ri-attachment-line mr-1.5 text-gray-400"></i>
                                      <span className="truncate">{dokument.file_name}</span>
                                    </p>
                                  )}
                                  
                                  {dokument.automat && (
                                    <p className="text-xs text-blue-600 flex items-center">
                                      <i className="ri-robot-line mr-1.5"></i>
                                      {dokument.automat}
                                    </p>
                                  )}
                                  
                                  {dokument.notes && (
                                    <p className="text-xs text-gray-600 line-clamp-2">{dokument.notes}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Mobile-optimized List View
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {gefilterte_dokumente.map((dokument) => (
                    <div key={dokument.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-file-text-line text-blue-600 text-sm"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{dokument.title}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(dokument.document_date).toLocaleDateString('de-DE')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          {dokument.file_name && dokument.file_url && (
                            <button 
                              onClick={() => handleDownload(dokument.file_url!, dokument.file_name!)}
                              className="p-2 text-gray-400 hover:text-blue-600 cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
                              title="Herunterladen"
                            >
                              <i className="ri-download-line text-sm"></i>
                            </button>
                          )}
                          <button 
                            onClick={() => handleEdit(dokument)}
                            className="p-2 text-gray-400 hover:text-green-600 cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
                            title="Bearbeiten"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button 
                            onClick={() => handleDelete(dokument.id)}
                            className="p-2 text-gray-400 hover:text-red-600 cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
                            title="Löschen"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {dokument.file_name && (
                          <p className="text-xs text-gray-600 flex items-center">
                            <i className="ri-attachment-line mr-1.5 text-gray-400"></i>
                            <span className="truncate">{dokument.file_name}</span>
                          </p>
                        )}
                        
                        {dokument.automat && (
                          <p className="text-xs text-blue-600 flex items-center">
                            <i className="ri-robot-line mr-1.5"></i>
                            {dokument.automat}
                          </p>
                        )}
                        
                        {dokument.notes && (
                          <p className="text-xs text-gray-600 line-clamp-2">{dokument.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile-optimized Modal für neues Dokument */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Neues Dokument hinzufügen"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Titel"
            required
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Dokumententitel eingeben..."
          />

          <Select
            label="Kontakt"
            required
            options={kontaktOptionen}
            value={formData.contact}
            onChange={(value) => setFormData(prev => ({ ...prev, contact: value as string }))}
            placeholder="Kontakt auswählen..."
          />

          <Select
            label="Kategorie"
            required
            options={kategorieOptionen}
            value={formData.category}
            onChange={(value) => setFormData(prev => ({ ...prev, category: value as string }))}
            placeholder="Kategorie auswählen..."
          />

          <Select
            label="Automat (optional)"
            options={automatenOptionen}
            value={formData.automat}
            onChange={(value) => setFormData(prev => ({ ...prev, automat: value as string }))}
            placeholder="Automat auswählen..."
          />

          <Input
            label="Datum"
            type="date"
            required
            value={formData.document_date}
            onChange={(e) => setFormData(prev => ({ ...prev, document_date: e.target.value }))}
          />

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Datei
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
            />
            <p className="mt-2 text-xs text-gray-500">
              PDF, Word, Excel oder Bild-Dateien
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notiz
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Zusätzliche Informationen..."
              rows={4}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium whitespace-nowrap text-sm"
              disabled={uploading}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium whitespace-nowrap text-sm"
              disabled={uploading || !formData.title || !formData.contact || !formData.category}
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Speichere...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  Speichern
                </>
              )}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Mobile-optimized Modal für Dokument bearbeiten */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingDocument(null);
        }}
        title="Dokument bearbeiten"
        maxWidth="lg"
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <Input
            label="Titel"
            required
            value={editFormData.title}
            onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Dokumententitel eingeben..."
          />

          <Select
            label="Kontakt"
            required
            options={kontaktOptionen}
            value={editFormData.contact}
            onChange={(value) => setEditFormData(prev => ({ ...prev, contact: value as string }))}
            placeholder="Kontakt auswählen..."
          />

          <Select
            label="Kategorie"
            required
            options={kategorieOptionen}
            value={editFormData.category}
            onChange={(value) => setEditFormData(prev => ({ ...prev, category: value as string }))}
            placeholder="Kategorie auswählen..."
          />

          <Select
            label="Automat (optional)"
            options={automatenOptionen}
            value={editFormData.automat}
            onChange={(value) => setEditFormData(prev => ({ ...prev, automat: value as string }))}
            placeholder="Automat auswählen..."
          />

          <Input
            label="Datum"
            type="date"
            required
            value={editFormData.document_date}
            onChange={(e) => setEditFormData(prev => ({ ...prev, document_date: e.target.value }))}
          />

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notiz
            </label>
            <textarea
              value={editFormData.notes}
              onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Zusätzliche Informationen..."
              rows={4}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
            />
          </div>

          {editingDocument?.file_name && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <i className="ri-file-line text-gray-500"></i>
                <span className="text-sm text-gray-700 truncate">Aktuelle Datei: {editingDocument.file_name}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Hinweis: Die Datei kann nicht geändert werden. Erstellen Sie ein neues Dokument für eine andere Datei.
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingDocument(null);
              }}
              className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium whitespace-nowrap text-sm"
              disabled={updating}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium whitespace-nowrap text-sm"
              disabled={updating || !editFormData.title || !editFormData.contact || !editFormData.category}
            >
              {updating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Aktualisiere...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  Änderungen speichern
                </>
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
